import type { InputBaseProps } from '@mui/material';
import { Box } from '@mui/material';
import type { FormEvent } from 'react';
import React, { useImperativeHandle, useState } from 'react';
import type { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { MbFormItem } from './MbFormItem';
import type { InputPropsType, MbFormFieldType, MbSelectProps, SelectOptions } from './type';

declare module 'react' {
  function forwardRef<T, P = object>(
    render: (props: P, ref: React.Ref<T>) => React.ReactNode | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactNode | null;
}

export type MbFormProps<T extends FieldValues> = {
  fields: MbFormFieldType[];
  column?: number | 'auto';
  itemMinWith?: number;
  gap?: number | string;
  buttonArea?: React.ReactNode;
  layout?: 'horizontal' | 'vertical';
  size?: Pick<InputBaseProps, 'size'>['size'];
  defaultValues: T;
  onSubmit?: (data: T) => void;
  renderSlots?: Record<string, React.ReactNode>;
};

export type MbFormRef<T extends FieldValues> = UseFormReturn<T> & {
  setFieldChoices: (name: keyof T, choices: SelectOptions[]) => void;
  setFieldDisabled: (name: keyof T, disabled: boolean) => void;
  setFieldError: (name: keyof T, message: string) => void;
  clearFieldError: (name: keyof T) => void;
  clearError: () => void;
  setFieldInputProps: <K extends keyof InputPropsType>(name: keyof T, itemProps: InputPropsType[K]) => void;
};

const MbFormInner = <T extends FieldValues>(props: MbFormProps<T>, ref: React.ForwardedRef<MbFormRef<T>>) => {
  const { fields, buttonArea, column, itemMinWith, gap, size = 'small', defaultValues, onSubmit, renderSlots } = props;
  const [formFields, setFormFields] = useState<MbFormFieldType[]>(fields);

  const form = useForm<T>({ defaultValues: defaultValues as DefaultValues<T> });
  const { control, handleSubmit } = form;
  const setFieldChoices = (name: keyof T, choices: SelectOptions[]) => {
    setFormFields((tempFields) =>
      tempFields.map((field) => {
        const tempField = { ...field };
        if (tempField.name === name) {
          tempField.itemProps = {
            ...((tempField.itemProps as MbSelectProps) || {}),
            choices,
          };
        }

        return tempField;
      }),
    );
  };

  const setFieldDisabled = (name: keyof T, disabled: boolean) => {
    setFormFields((tempFields) =>
      tempFields.map((field) => {
        const tempField = { ...field };
        if (tempField.name === name) {
          tempField.itemProps = { ...(tempField.itemProps || {}), disabled };
        }

        return tempField;
      }),
    );
  };

  const setFieldError = (name: keyof T, message: string) => {
    setFormFields((tempFields) =>
      tempFields.map((field) => {
        const tempField = { ...field };
        if (tempField.name === name) {
          tempField.error = true;
          tempField.errorMsg = message;
        }

        return tempField;
      }),
    );
  };

  const clearFieldError = (name: keyof T) => {
    setFormFields((tempFields) =>
      tempFields.map((field) => {
        const tempField = { ...field };
        if (tempField.name === name) {
          tempField.error = false;
          tempField.errorMsg = '';
        }

        return tempField;
      }),
    );
  };

  const clearError = () => {
    setFormFields((tempFields) =>
      tempFields.map((field) => {
        const tempField = { ...field, error: false, errorMsg: '' };

        return tempField;
      }),
    );
  };

  const setFieldInputProps = <K extends keyof InputPropsType>(name: keyof T, itemProps: InputPropsType[K]) => {
    setFormFields((tempFields) =>
      tempFields.map((field) => {
        const tempField = { ...field };
        if (tempField.name === name) {
          tempField.itemProps = {
            ...(tempField.itemProps || {}),
            ...(itemProps as object),
          };
        }

        return tempField;
      }),
    );
  };

  useImperativeHandle(ref, () => ({
    ...form,
    setFieldChoices,
    setFieldDisabled,
    setFieldError,
    clearError,
    clearFieldError,
    setFieldInputProps,
  }));

  let gridTemplateColumns = `repeat(auto-fit, minmax(${itemMinWith}px, 1fr))`;
  if (column !== 'auto') {
    gridTemplateColumns = `repeat(${column},1fr)`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const colMap: Record<string, any> = {};

  if (typeof column === 'number') {
    let spanColumnCnt = 0;

    fields.forEach((item, index) => {
      let spanColumn = item.spanColumn || 1;
      const beforeSpan = item.beforeSpan || 0;
      const afterSpan = item.afterSpan || 0;
      const startCol = (index + 1 + beforeSpan + spanColumnCnt) % column || column;
      let endCol = startCol + spanColumn;
      if (endCol > column + 1) {
        endCol = column + 1;
        spanColumn = endCol - startCol;
      } else if (endCol + afterSpan > column + 1) {
        endCol = column + 1;
        spanColumn = endCol + afterSpan - startCol;
      } else {
        spanColumn = endCol - startCol + beforeSpan + afterSpan;
      }
      spanColumnCnt = spanColumnCnt + spanColumn - 1;

      colMap[item.name] = {
        startCol,
        endCol,
      };
    });
  }

  const submitHandle = (data: T) => {
    onSubmit?.(data);
  };

  const formHandleSubmit = handleSubmit(submitHandle);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void formHandleSubmit();
  };

  return (
    <form onSubmit={submit}>
      <Box display="flex" flexDirection="column" rowGap={1}>
        <Box display="grid" gridTemplateColumns={gridTemplateColumns} width="100%" gap={gap}>
          {formFields.map((field) => (
            <MbFormItem
              control={control}
              size={size}
              key={field.name}
              {...field}
              onChange={(value) => {
                field.onChange?.(value);
                clearFieldError(field.name);
              }}
              {...colMap[field.name]}
              render={renderSlots?.[field.name]}
            />
          ))}
        </Box>
        {buttonArea}
      </Box>
    </form>
  );
};

export const MbForm = React.forwardRef(MbFormInner);
